// Core imports
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useTheme from '../../../utils/hooks/useTheme';

// Component imports
import { Badge, Button, Card, Col, Divider, Form, Popconfirm, Popover, Radio, Row, Select, Slider, Space, Switch, Tooltip, Typography } from 'antd';
import Prompt from '../../UI/Prompt';
import { CloseOutlined, FilterOutlined, QuestionCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import CustomInputNumber from '../Fields/CustomInputNumber';
import RichTextEditor from '../Fields/RichTextEditor/RichTextEditor';
import { DeleteIcon } from '../../Icons/DeleteIcon';
import { PlusIcon } from '../../Icons/PlusIcon';

// Misc imports
import { INTEGRATION_CATEGORY, INTEGRATION_TYPE, PLUGIN_CATEGORY } from '../../../constants/integrations';
import { getCurrentQueryState } from '../../../store/utils/getCurrentQueryState';
import { useFetchBotDefaultPromptQuery, useFetchBotsQuery, useUpdateBotMutation } from '../../../store/bots/botsApi';
import { OPEN_AI_COMPLETION_MODELS, GEMINI_MODELS, SESSION_ANALYSIS_EXPERT_MODE_DEFAULT_INITIAL_VALUE, SESSION_ANALYSIS_MODES } from '../../../constants/main';
import { RICH_TEXT_TOOLBAR_ACTIONS } from '../../../constants/markdown';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_CURLY_REGEX, ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../../constants/regex';
import { useFetchIntegrationsQuery } from '../../../store/integrations/integrationsApi';
import { API_TAGS } from '../../../constants/api';

const PluginSummarizationForm = ({
  form,
  onSuccess,
  onCanSubmitChange,
  onLoadingChange,
  integrationType
}) => {

  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const { formProps, borderRadiusBase } = useTheme();
  const { data: fetchedBot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: bots } = useFetchBotsQuery({ orgId, urlParams: { includePlugins: true } }, { skip: !orgId || tenantId });
  const { data: botDefaultPrompt } = useFetchBotDefaultPromptQuery({ orgId, category: PLUGIN_CATEGORY.LLM_SUMMARIZATION }, { skip: !orgId || !tenantId });
  const { data: integrationsData, isFetching: isFetchingIntegrations } = useFetchIntegrationsQuery({ orgId, urlParams: { type: integrationType } }, { skip: !orgId });

  const bot = useMemo(() => fetchedBot || (bots?.items || []).find(bot => bot.id === form.getFieldValue('bot')), [fetchedBot, bots, form.getFieldValue('bot')]);
  const temperatureSliderRef = useRef(null);

  const summarizationPlugin = useMemo(() => (bot?.plugins || []).find(plugin => plugin.integration.category === INTEGRATION_CATEGORY.LLM && plugin.category === PLUGIN_CATEGORY.LLM_SUMMARIZATION), [bot]);
  const [, updateState] = useState(); // Hook to force a component re-render
  const [updateBot, { isLoading: isUpdatingBot }] = useUpdateBotMutation();
  const [canSubmit, setCanSubmit] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(!summarizationPlugin);
  const [localModels, setLocalModels] = useState([]);

  const sessionAnalysisExpertModeDefaultValues = useMemo(() => {
    if(botDefaultPrompt?.prompt) {
      return {
        ...SESSION_ANALYSIS_EXPERT_MODE_DEFAULT_INITIAL_VALUE,
        prompt: botDefaultPrompt?.prompt
      };
    }
    return SESSION_ANALYSIS_EXPERT_MODE_DEFAULT_INITIAL_VALUE;
  }, [botDefaultPrompt]);

  const integrationOptions = useMemo(() => {
    return (integrationsData && integrationsData?.items?.map(int => ({ label: int.name, value: int.id }))) ?? [];
  }, [integrationsData]);

  const isGeminiIntegration = (summarizationPlugin?.integration?.type) === INTEGRATION_TYPE.GEMINI_AI_LLM_SUMMARIZATION;
  const isAzureIntegration = (summarizationPlugin?.integration?.type) === INTEGRATION_TYPE.AZURE_AI_LLM_SUMMARIZATION;

  const currentModelListToBeUsed = useMemo(() => {
    return isGeminiIntegration ? GEMINI_MODELS : OPEN_AI_COMPLETION_MODELS;
  }, [isGeminiIntegration]);

  useEffect(() => {
    form.resetFields();
  }, [fetchedBot?.id]);

  useEffect(() => {
    setLocalModels(Object.values(currentModelListToBeUsed));
  }, []);

  const updateModels = (newModelName) => {
    if (newModelName && Object.values(currentModelListToBeUsed).filter(model => model === newModelName).length === 0) {
      setLocalModels([...new Set([...Object.values(currentModelListToBeUsed), newModelName])]);
    }
  };

  useEffect(() => {
    // Reset the form fields when the bot changes (except for the bot field itself)
    form.resetFields(Object.keys(form.getFieldsValue()).filter(field => field !== 'bot'));
  }, [bot?.id]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isUpdatingBot);
    }
  }, [isUpdatingBot]);

  const updateCanSubmit = async () => {
    try {
      const { errorFields } = await form.validateFields(form?.getFieldValue('mode') === SESSION_ANALYSIS_MODES.BASIC ? ['model'] : ['requests']);
      const formHasErrors = errorFields?.length > 0;
      setCanSubmit(!formHasErrors);
      if (onCanSubmitChange) {
        onCanSubmitChange(!formHasErrors);
      }
    } catch(e) {
      const formHasErrors = e.errorFields?.length > 0;
      setCanSubmit(!formHasErrors);
      if (onCanSubmitChange) {
        onCanSubmitChange(!formHasErrors);
      }
    }
  };

  const onSubmit = async (values) => {
    let isSubmitSuccessful = true;

    if (settingsChanged) {
      const { integrationId, ...settingsValues } = values;
      let newPlugins = [...(bot?.plugins || [])];

      const existingPluginIndex = newPlugins.findIndex(
        p => p.category === PLUGIN_CATEGORY.LLM_SUMMARIZATION
          && (p.integration?.type === integrationType || p.integrationId === integrationId)
      );

      const newPluginData = {
        integrationId: integrationId,
        category: PLUGIN_CATEGORY.LLM_SUMMARIZATION,
        settings: existingPluginIndex >= 0
          ? { ...newPlugins[existingPluginIndex].settings, ...settingsValues }
          : settingsValues
      };

      if (existingPluginIndex >= 0) {
        newPlugins[existingPluginIndex] = newPluginData;
      } else {
        newPlugins.push(newPluginData);
      }

      const safePlugins = newPlugins.map(p => ({
        integrationId: p.integrationId || p.integration?.id,
        category: p.category,
        settings: p.settings
      }));

      isSubmitSuccessful = await updateBot({
        orgId,
        tenantId: bot?.id,
        data: { plugins: safePlugins },
        resourcesToInvalidate: [API_TAGS.NLP_PIPELINES]
      }).unwrap();
    }

    if (isSubmitSuccessful) {
      setCanSubmit(false);
      setSettingsChanged(false);
      if (onSuccess) {
        onSuccess(values);
      }
    }
  };

  return (
    <Fragment>
      <Form
        form={form}
        onFinish={onSubmit}
        layout="vertical"
        onValuesChange={(changedValue, allFields) => {
          const fieldIsBot = changedValue.bot;

          if(Reflect.has(changedValue, 'mode')) {
            const currentModeStringValue = changedValue.mode ? SESSION_ANALYSIS_MODES.EXPERT : SESSION_ANALYSIS_MODES.BASIC;
            form.setFieldValue('mode', currentModeStringValue);
          }
          if (!fieldIsBot) {
            setSettingsChanged(true);
            (async () => {
              await updateCanSubmit();
            })();
          }
          updateState(allFields);
        }}
        initialValues={{
          integrationId: summarizationPlugin?.integration?.id || null,
          ...summarizationPlugin?.settings,
          mode: summarizationPlugin?.settings?.mode || SESSION_ANALYSIS_MODES.BASIC
        }}
      >
        <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
          <Col xs={formProps.mainColSpan}>
            <Typography.Text>{t('Integration Configuration')}</Typography.Text>
            <Typography.Paragraph className="fs-sm" type="secondary">
              {t('Select which available integration this agent plugin will use. Changing the integration does not cause downtime for the plugin.')}
            </Typography.Paragraph>
          </Col>
          <Col xs={formProps.secondaryColSpan}>
            <Form.Item
              name="integrationId"
              label={t('Select Integration')}
              rules={[{ required: true, message: t('Please select an integration.') }]}
            >
              <Select
                placeholder={t('Select Integration')}
                loading={isFetchingIntegrations}
                options={integrationOptions}
              />
            </Form.Item>
          </Col>
        </Row>
        <Divider className="my-4"/>
        <div className="position-absolute" style={{ top: 15, right: 30 }}>
          <Space direction="horizontal">
            <Typography.Text>
              {t('Expert Mode')}
            </Typography.Text>
            <Form.Item
              className="m-0 label-p-0"
              name="mode"
            >
              <Switch checked={form.getFieldValue('mode') === SESSION_ANALYSIS_MODES.EXPERT} className="float-right" />
            </Form.Item>
          </Space>
        </div>
        <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
          <Col xs={formProps.mainColSpan}>
            <Typography.Text>
              {t('Session Analysis mode')}
            </Typography.Text>
            <Typography.Paragraph className="fs-sm" type="secondary">
              {t('Choose whether analysis runs automatically after each session or on demand when needed.')}
            </Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan}>
            <Form.Item
              name="automaticallyRunOnSessionCompletion"
              className="mb-2"
            >
              <Radio.Group
                className="d-flex flex-column"
                defaultValue={summarizationPlugin?.settings?.automaticallyRunOnSessionCompletion ?? false}
              >
                <Radio className="mb-2" value={false}>{t('On demand')}</Radio>
                <Radio value={true}>{t('Automatically')}</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        <Divider className="my-4"/>
        {
          form.getFieldValue('mode') === SESSION_ANALYSIS_MODES.BASIC ? (
            <Fragment>
              <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
                <Col xs={formProps.mainColSpan}>
                  <Typography.Text>
                    {t('Insights')}
                  </Typography.Text>
                  <Typography.Paragraph className="fs-sm" type="secondary">
                    {t('These insights can be viewed in Agent Records - Session Details.')}
                  </Typography.Paragraph>
                </Col>
                <Col xs={formProps.secondaryColSpan}>
                  <Space direction="vertical" className="w-100" size="middle">
                    <Typography.Text>
                      {`1. ${t('Summary')}`}
                      <Tooltip
                        className="ml-1"
                        placement="top"
                        title={t(`Generates a concise session summary. Summary can be generated automatically when a conversation is finished or transferred to another LiveChat Agent (in case of Livechat) or on demand. Although you can create a summary at any time during the conversation.`)}
                      >
                        <QuestionCircleOutlined className="fs-sm position-absolute" />
                      </Tooltip>
                    </Typography.Text>
                    <Form.Item
                      label={
                        <Row>
                          <Col span={24}>
                            <Typography.Text>
                              {`2. ${t('Session Classification Tags')}`}
                              <Tooltip
                                className="ml-1"
                                placement="top"
                                title={t('Labels conversations with relevant categories. Matching tags will be automatically added to chat-session tags.')}
                              >
                                <QuestionCircleOutlined className="fs-sm position-absolute" />
                              </Tooltip>
                            </Typography.Text>
                          </Col>
                        </Row>}
                      name="sessionClassificationTags"
                      className="mb-2"
                      labelAlign="left"
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                    >
                      <Select
                        mode="tags"
                        size="large"
                        tokenSeparators={[',']}
                        placeholder={t('Set tags for summarization classification.')}
                        options={
                          summarizationPlugin?.settings?.sessionClassificationTags?.length > 0 ? summarizationPlugin?.settings?.sessionClassificationTags?.map((tag)=> {
                            return {
                              value: tag,
                              label: tag
                            };
                          }) : null
                        }
                        onInputKeyDown={(e) => {
                          if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                            e.preventDefault();
                            return false;
                          }
                        }}
                        maxTagPlaceholder={(omittedValues) => (
                          <Tooltip
                            placement="bottomLeft"
                            title={
                              <div className="overflow-y-auto overflow-x-hidden scrollbar-custom" style={{ maxHeight: 280, maxWidth: 220 }}>
                                {
                                  omittedValues.map((value, index) => (
                                    <Typography.Text.Paragraph ellipsis key={index} className="fs-base text-muted mb-0">
                                      {value.label}

                                      <Button
                                        size="small"
                                        type="link"
                                        className="mb-1"
                                        icon={<CloseOutlined className="text-secondary mr-0 fs-sm" />}
                                      />
                                    </Typography.Text.Paragraph>
                                  ))
                                }
                              </div>
                            }
                          >
                        +{omittedValues.length}
                          </Tooltip>
                        )}
                        // Fix drop-menu position issues by rendering it in the parent element
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                      />
                    </Form.Item>
                    <div>
                      <Typography.Text className="d-block mb-1">
                        {`3. ${t('Sentiment')}`}
                        <Tooltip
                          className="ml-1"
                          placement="top"
                          title={t(`Evaluates user satisfaction.`)}
                        >
                          <QuestionCircleOutlined className="fs-sm position-absolute" />
                        </Tooltip>
                      </Typography.Text>
                      <Space wrap size="middle">
                        <Badge status="success" text={<Typography.Text type="secondary">{t('Positive')}</Typography.Text>} />
                        <Badge status="warning" text={<Typography.Text type="secondary">{t('Neutral')}</Typography.Text>} />
                        <Badge status="error" text={<Typography.Text type="secondary">{t('Negative')}</Typography.Text>} />
                      </Space>
                    </div>
                    <div>
                      <Typography.Text className="d-block mb-1">
                        {`4. ${t('Resolution')}`}
                        <Tooltip
                          className="ml-1"
                          placement="top"
                          title={t('Indicates issue status.')}
                        >
                          <QuestionCircleOutlined className="fs-sm position-absolute" />
                        </Tooltip>
                      </Typography.Text>
                      <Space wrap size="middle">
                        <Badge status="success" text={<Typography.Text type="secondary">{t('Resolved')}</Typography.Text>} />
                        <Badge status="warning" text={<Typography.Text type="secondary">{t('Unclear')}</Typography.Text>} />
                        <Badge status="error" text={<Typography.Text type="secondary">{t('Unresolved')}</Typography.Text>} />
                      </Space>
                    </div>
                    <div>
                      <Typography.Text className="d-block mb-1">
                        {`5. ${t('Urgency')}`}
                        <Tooltip
                          className="ml-1"
                          placement="top"
                          title={t('Determines priority level.')}
                        >
                          <QuestionCircleOutlined className="fs-sm position-absolute" />
                        </Tooltip>
                      </Typography.Text>
                      <Space wrap size="middle">
                        <Badge status="success" text={<Typography.Text type="secondary">{t('Low')}</Typography.Text>} />
                        <Badge status="warning" text={<Typography.Text type="secondary">{t('Normal')}</Typography.Text>} />
                        <Badge status="error" text={<Typography.Text type="secondary">{t('Urgent')}</Typography.Text>} />
                      </Space>
                    </div>
                  </Space>

                </Col>
              </Row>
              <Divider className="my-4"/>
              <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
                <Col xs={formProps.mainColSpan}>
                  <Typography.Text>
                    {t('LLM Configuration')}
                  </Typography.Text>
                  <Typography.Paragraph className="fs-sm" type="secondary">
                    {t(`Select the preferred LLM ${(summarizationPlugin?.integration?.type) !== INTEGRATION_TYPE.AZURE_AI_LLM_SUMMARIZATION ? t('Model') : t('Deployment')} and configure advanced settings.`)}
                  </Typography.Paragraph>
                </Col>

                <Col xs={formProps.secondaryColSpan}>
                  <Form.Item
                    required
                    name={['model']}
                    rules={[
                      {
                        required: true,
                        message: t('Field cannot be blank.')
                      }]}
                    label={isAzureIntegration ? t('Deployment') : t('Model') }
                    className="mb-0"
                  >
                    <Select
                      showSearch
                      onSearch={updateModels}
                      onBlur={(e) => updateModels(e.target.value)}
                      placeholder={t('Type / Select model')}
                      getPopupContainer={triggerNode => triggerNode.parentElement}
                      options={(localModels || [])
                        .map((element) => {
                          return {
                            value: element,
                            label: element
                          };
                        })
                      // Keep only unique values
                        .filter((elem, index, self) => self.findIndex((t) => {
                          return (t.value === elem.value);
                        }) === index)
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Fragment>
          ) : (
            <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
              <Col xs={formProps.mainColSpan}>
                <Typography.Text>
                  {t('LLM Configuration')}
                </Typography.Text>
                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t(`Select the preferred LLM ${isAzureIntegration ? t('Deployment') : t('Model') } and configure advanced settings.`)}
                </Typography.Paragraph>
              </Col>
              <Col xs={formProps.secondaryColSpan}>
                <Form.Item
                  shouldUpdate
                  className="mb-2 py-2 "
                >
                  {() => (
                    <Form.List
                      name="requests"
                    >
                      {(fields, { add: addRequest, remove: removeRequest }) => (
                        <Fragment>
                          {fields?.length === 0 && addRequest(sessionAnalysisExpertModeDefaultValues) }
                          {fields.map((field, requestIndex) => (
                            <Row
                              key={field.key}
                              gutter={[8, 8]}
                              align="start"
                              wrap={false}
                            >
                              <Col xs={22}>
                                <Card
                                  className="bg-layout mb-3 shadow-none"
                                >
                                  <Row justify="end">
                                    <Col>
                                      <Popover
                                        showArrow={false}
                                        trigger="click"
                                        placement="bottomRight"
                                        overlayInnerStyle={{ width: 340 }}
                                        overlayClassName="ant-popover-inner-content-max-width"
                                        content={
                                          <div
                                            className="overflow-y-auto overflow-x-hidden"
                                            style={{ maxHeight: 500 }}>
                                            <Row>
                                              <Col span={24}>
                                                <Space direction="horizontal" size="small" className="mb-3">
                                                  <Typography.Text strong>
                                                    {t('LLM Applied to Filtered Sessions:')}
                                                  </Typography.Text>
                                                  <Tooltip title={t('Configure which sessions this analysis runs on based on session tags.')}>
                                                    <QuestionCircleOutlined className="fs-sm text-secondary" />
                                                  </Tooltip>
                                                </Space>

                                                <Form.Item
                                                  label={t('Included Tags')}
                                                  tooltip={{
                                                    icon: <QuestionCircleOutlined />,
                                                    title: t('The analysis will run on sessions that explicitly include this tags.')
                                                  }}
                                                  name={[field.name, 'includedTags']}
                                                  labelCol={{ span: 24 }}
                                                  wrapperCol={{ span: 24 }}
                                                >
                                                  <Select
                                                    defaultValue={[]}
                                                    mode="tags"
                                                    size="large"
                                                    className="scrollable-tags-list"
                                                    tokenSeparators={[',']}
                                                    placeholder={t('Add tags')}
                                                    options={
                                                      form.getFieldValue([field.name, 'includedTags'])?.length > 0 ? form.getFieldValue([field.name, 'includedTags'])?.map((tag) => {
                                                        return {
                                                          value: tag,
                                                          label: tag
                                                        };
                                                      }) : []
                                                    }
                                                    onInputKeyDown={(e) => {
                                                      if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_CURLY_REGEX.test(e.key)) {
                                                        e.preventDefault();
                                                        return false;
                                                      }
                                                    }}
                                                    maxTagPlaceholder={(omittedValues) => (
                                                      <Tooltip
                                                        title={
                                                          <div
                                                            className="overflow-y-auto overflow-x-hidden scrollbar-custom"
                                                            style={{ maxHeight: 220, maxWidth: 220 }}
                                                          >
                                                            {
                                                              omittedValues.map((value, index) => (
                                                                <Typography.Paragraph ellipsis key={index} className="fs-base text-muted mb-0">
                                                                  {value.label}

                                                                  <Button
                                                                    size="small"
                                                                    type="link"
                                                                    className="mb-1"
                                                                    icon={<CloseOutlined className="text-secondary mr-0 fs-sm" />}
                                                                  />
                                                                </Typography.Paragraph>
                                                              ))
                                                            }
                                                          </div>
                                                        }
                                                        placement="bottomLeft"
                                                      >
                          +{omittedValues.length}
                                                      </Tooltip>
                                                    )}
                                                  />
                                                </Form.Item>

                                                <Form.Item
                                                  label={t('Excluded Tags')}
                                                  tooltip={{
                                                    icon: <QuestionCircleOutlined />,
                                                    title: t('The analysis will not run on sessions that include this tags.')
                                                  }}
                                                  name={[field.name, 'excludedTags']}
                                                  labelCol={{ span: 24 }}
                                                  wrapperCol={{ span: 24 }}
                                                >
                                                  <Select
                                                    mode="tags"
                                                    size="large"
                                                    defaultValue={[]}
                                                    className="scrollable-tags-list"
                                                    tokenSeparators={[',']}
                                                    placeholder={t('Add tags')}
                                                    onInputKeyDown={(e) => {
                                                      if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_CURLY_REGEX.test(e.key)) {
                                                        e.preventDefault();
                                                        return false;
                                                      }
                                                    }}
                                                    options={
                                                      form.getFieldValue([field.name, 'excludedTags'])?.length > 0 ? form.getFieldValue([field.name, 'excludedTags'])?.map((tag) => {
                                                        return {
                                                          value: tag,
                                                          label: tag
                                                        };
                                                      }) : []
                                                    }
                                                    maxTagPlaceholder={(omittedValues) => (
                                                      <Tooltip
                                                        placement="bottomLeft"
                                                        title={
                                                          <div
                                                            className="overflow-y-auto overflow-x-hidden scrollbar-custom"
                                                            style={{ maxHeight: 220, maxWidth: 220 }}
                                                          >
                                                            {
                                                              omittedValues.map((value, index) => (
                                                                <Typography.Paragraph ellipsis key={index} className="fs-base text-muted mb-0">
                                                                  {value.label}

                                                                  <Button
                                                                    size="small"
                                                                    type="link"
                                                                    className="mb-1"
                                                                    icon={<CloseOutlined className="text-secondary mr-0 fs-sm" />}
                                                                  />
                                                                </Typography.Paragraph>
                                                              ))
                                                            }
                                                          </div>
                                                        }
                                                      >
                                                        +{omittedValues.length}
                                                      </Tooltip>
                                                    )}
                                                  />
                                                </Form.Item>
                                              </Col>
                                            </Row>
                                          </div>
                                        }
                                      >
                                        <Tooltip title={t('Applied to Sessions')}>
                                          <Button
                                            type="text"
                                            size="small"
                                            className="text-primary"
                                          >
                                            <FilterOutlined /> {t('Applied to Sessions')}
                                          </Button>
                                        </Tooltip>
                                      </Popover>
                                    </Col>
                                  </Row>
                                  <Row gutter={16}>
                                    <Col xs={24}>
                                      <Form.Item
                                        {...field}
                                        required
                                        name={[field.name, 'model']}
                                        rules={[
                                          {
                                            required: true,
                                            message: t('Field cannot be blank.')
                                          }]}
                                        label={isAzureIntegration ? t('Deployment') : t('Model') }
                                      >
                                        <Select
                                          showSearch
                                          onSearch={updateModels}
                                          onBlur={(e) => updateModels(e.target.value)}
                                          placeholder={t('Type / Select model')}
                                          getPopupContainer={triggerNode => triggerNode.parentElement}
                                          options={(localModels || [])
                                            .map((element) => {
                                              return {
                                                value: element,
                                                label: element
                                              };
                                            })
                                          // Keep only unique values
                                            .filter((elem, index, self) => self.findIndex((t) => {
                                              return (t.value === elem.value);
                                            }) === index)
                                          }
                                        />
                                      </Form.Item>
                                    </Col>
                                    <Col span={24}>
                                      <Form.Item
                                        {...field}
                                        label={t('Prompt')}
                                        name={[field.name, 'prompt']}
                                        required
                                        rules={[
                                          {
                                            required: true,
                                            message: t('Prompt cannot be blank.')
                                          },
                                          {
                                            validator: (_, value) => {
                                              const hasTranscriptKeyword = value && value.includes('{{transcript}}');

                                              const jsonInstructionPattern = /json/i;
                                              const hasJsonInstruction = jsonInstructionPattern.test(value);

                                              const jsonObjectPattern = /(?<!{){(?!{)[^]*?}/s;
                                              const hasJsonObject = value && jsonObjectPattern.test(value);

                                              const rejectWithError = (message) => {
                                                setCanSubmit(false);
                                                return Promise.reject(new Error(message));
                                              };

                                              if (!hasJsonInstruction) {
                                                return rejectWithError('In order to submit, you must instruct the agent to respond in JSON.');
                                              }
                                              if (!hasJsonObject) {
                                                return rejectWithError('In order to submit, you must include a JSON-like object in your prompt.');
                                              }

                                              if (!hasTranscriptKeyword) {
                                                return rejectWithError('The prompt must contain the literal "{{transcript}}".');
                                              }

                                              setCanSubmit(true);
                                              return Promise.resolve();
                                            }
                                          }
                                        ]}
                                      >
                                        <RichTextEditor
                                          toolbarActions={[
                                            RICH_TEXT_TOOLBAR_ACTIONS.HEADING,
                                            RICH_TEXT_TOOLBAR_ACTIONS.BOLD,
                                            RICH_TEXT_TOOLBAR_ACTIONS.ITALIC,
                                            RICH_TEXT_TOOLBAR_ACTIONS.BULLET_LIST,
                                            RICH_TEXT_TOOLBAR_ACTIONS.ORDERED_LIST
                                          ]}
                                          shouldAutoFocus={false}
                                          copySourceButton
                                          style={{ maxHeight: 260, minHeight: 260 }}
                                          placeholder={t('Enter the prompt')}
                                          elementStyles={{ p: { marginBottom: 0 } }}
                                        />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                      <div ref={temperatureSliderRef}>
                                        <Form.Item
                                          label={t('Temperature')}
                                          name={[field.name, 'temperature']}
                                        >
                                          <Slider
                                            min={0}
                                            max={2}
                                            step={0.1}
                                            className="custom-slider mx-6"
                                            marks={{ 0: '0', 2: '2' }}
                                            handleStyle={{ borderRadius: borderRadiusBase }}
                                            onChange={(value) => {
                                              const sliderHandler = temperatureSliderRef.current.querySelector('.ant-slider-handle');
                                              if (!sliderHandler) {
                                                return;
                                              }
                                              sliderHandler.innerHTML = value;
                                            }}
                                          />
                                        </Form.Item>
                                      </div>
                                    </Col>
                                    <Col xs={24}>
                                      <Form.Item
                                        label={t('Max Tokens')}
                                        name={[field.name, 'maxTokens']}
                                        tooltip={{
                                          title: (
                                            <Typography.Text type="secondary">{t('The maximum number of tokens to use when invoking the LLM.')}</Typography.Text>
                                          ),
                                          icon: <InfoCircleOutlined />
                                        }}
                                      >
                                        <CustomInputNumber min={1} defaultValue={50} step={1} />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </Card>
                              </Col>
                              {
                                fields.length > 0 ? (
                                  <Col flex="none">
                                    <Popconfirm
                                      title={t('Delete this LLM?')}
                                      okText={t('Yes')}
                                      cancelText={t('No')}
                                      disabled={fields?.length === 1}
                                      placement="topRight"
                                      onConfirm={() => {
                                        removeRequest(requestIndex);
                                      }}
                                    >
                                      <Button
                                        disabled={fields?.length === 1}
                                        type="text"
                                        shape="circle"
                                        className="text-primary"
                                        icon={<DeleteIcon />}
                                      />
                                    </Popconfirm>
                                  </Col>
                                ) : null
                              }
                            </Row>
                          ))}
                          <Row>
                            <Col xs={22}>
                              <Button

                                size="large"
                                type="dashed"
                                onClick={() => {
                                  addRequest(sessionAnalysisExpertModeDefaultValues);
                                }}
                                block
                                icon={<PlusIcon />}
                              >
                                {t('Add LLM')}
                              </Button>
                            </Col>
                          </Row>
                        </Fragment>
                      )}
                    </Form.List>
                  )
                  }
                </Form.Item>
              </Col>
            </Row>
          )
        }

      </Form>

      <Prompt
        when={canSubmit}
        onConfirm={() => form.submit()}
        okText={t('Save Changes')}
        cancelText={t('Quit without saving')}
        title={t('Save Changes?')}
        message={t('Your unsaved changes will be lost. Save changes before closing?')}
      />

      {!onSuccess ? (
        <Row justify="end" className="mt-4">
          <Col>
            <Button
              type="primary"
              size="large"
              onClick={() => form.submit()}
              disabled={!canSubmit}
            >
              {t('Save Changes')}
            </Button>
          </Col>
        </Row>
      ) : null}
    </Fragment>
  );
};

PluginSummarizationForm.propTypes = {
  form: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onCanSubmitChange: PropTypes.func,
  onLoadingChange: PropTypes.func,
  integrationType: PropTypes.string
};

export default PluginSummarizationForm;
